/**
 * ChatContactHeader.tsx
 *
 * Header showing contact info, avatar, and add/edit contact actions.
 * Extracted from ChatWrapper.v2.tsx.
 */

import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { KTSVG } from "src/resources/helpers/components/KTSVG";
import { Collaboration, Room } from "src/app/models/Chat";
import Customer from "src/app/models/Customer";
import { avatarUrl } from "src/app/modules/chat/utils";
import ChatRoomTabs from "./ChatRoomTabs";

type ChatContactHeaderProps = {
  collaboration: Collaboration;
  selectedRoom?: Room;
  contactData?: Customer;
  activeTab: "allChat" | "myChat";
  onBackClick?: () => void;
  onAddNewContact: () => void;
  onAddExistingContact: () => void;
  isMobile?: boolean;
};

type HiddenButton = {
  id: string;
  buttonText: string;
};

const ChatContactHeader: FC<ChatContactHeaderProps> = ({
  collaboration,
  selectedRoom,
  contactData,
  activeTab,
  onBackClick,
  onAddNewContact,
  onAddExistingContact,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const [dropdownHB, setDropdownHB] = useState<boolean>(false);

  const defaultAvatarName = "default-avatar";

  // Build chat title from contact data
  let chatTitle = "";
  if (contactData) {
    if (contactData.firstName && contactData.firstName !== "") {
      chatTitle = contactData.firstName;
      if (contactData.lastName && contactData.lastName !== "") {
        chatTitle = `${contactData.firstName} ${contactData.lastName}`;
      }
    } else {
      chatTitle = `+${contactData?.phoneNumber}`;
    }
  }

  // Get hidden buttons for overflow menu
  const getHiddenButtons = (): HiddenButton[] => {
    const container = document.getElementById("container-button");
    const buttons = container?.querySelectorAll(".btn");
    const hiddenButtons: HiddenButton[] = [];

    buttons?.forEach((button) => {
      const rect = button.getBoundingClientRect();
      if (container) {
        if (
          rect.left < container.getBoundingClientRect().left ||
          rect.right > container.getBoundingClientRect().right
        ) {
          hiddenButtons.push({
            id: button.id,
            buttonText: button?.textContent?.trim() || "",
          });
        }
      }
    });

    return hiddenButtons;
  };

  // Mobile menu action component
  const MenuAction = () => (
    <>
      <button
        data-testid="dropdownMenuAction"
        id="dropdownMenuLink"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        className="btn border border-0 p-0 m-0"
      >
        <KTSVG
          path="/media/icons/duotune/general/gen053.svg"
          className="svg-icon-1 m-0"
        />
      </button>
      <ul className="dropdown-menu" aria-labelledby="dropdownMenuLink">
        <li>
          <button
            data-testid="action-addToContacts"
            className="dropdown-item btn btn-light btn-active-light-primary btn-sm"
            data-bs-toggle="modal"
            data-bs-target="#ContactActionModal"
          >
            {t("HC.Button.AddToContacts")}
          </button>
        </li>
      </ul>
    </>
  );

  // Mobile layout
  if (isMobile) {
    return (
      <div
        id="kt_chat_messenger_header"
        className="d-flex flex-column px-2 py-1 m-0 w-100"
        style={{
          flexShrink: 0, // Prevent header from shrinking
          borderStyle: "solid",
          borderTopWidth: 0,
          borderLeftWidth: 0,
          borderRightWidth: 0,
          borderBottomWidth: "0.1px",
          borderColor: "rgba(161, 165, 183, 0.5)",
        }}
      >
        <div className="w-100 d-flex flex-column p-0 m-0">
          <div className="d-flex flex-row align-items-center justify-content-between p-0 m-0 w-100 gap-2">
            {/* Back button */}
            <svg
              onClick={onBackClick}
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 15 15"
              className="cursor-pointer"
            >
              <path
                fill="#009ef7"
                d="M6.707 2.293a1 1 0 0 0-1.414 0l-4.5 4.5a1 1 0 0 0 0 1.414l4.5 4.5a1 1 0 0 0 1.414-1.414L4 8.5h9.5a1 1 0 0 0 0-2H4l2.707-2.707a1 1 0 0 0 0-1.414Z"
              />
            </svg>

            {/* Avatar and name */}
            <div
              className="d-flex flex-row align-items-center h-auto p-0 m-0"
              style={{ width: "85%" }}
            >
              <span
                className="symbol symbol-28px symbol-circle d-flex align-items-center py-0 my-0"
                style={{ marginRight: "6px" }}
              >
                <img
                  alt={
                    collaboration?.customerModel
                      ? `${collaboration.customerModel.firstName} ${collaboration.customerModel.lastName}`
                      : defaultAvatarName
                  }
                  src={avatarUrl(collaboration?.customerModel)}
                />
              </span>
              <div className="h-auto d-flex justify-content-center flex-column">
                {contactData && contactData.id ? (
                  <Link
                    to={`/contact/detail/overview/${contactData.id}`}
                    state={{
                      id: contactData.id,
                      name:
                        contactData.firstName! +
                        (contactData.lastName
                          ? " " + contactData.lastName
                          : ""),
                    }}
                    className="fw-semibold text-gray-900 text-hover-primary h-auto"
                    style={{ fontSize: "13px" }}
                  >
                    {chatTitle}
                  </Link>
                ) : (
                  <div
                    className="fw-semibold text-gray-900 text-hover-primary h-auto"
                    style={{ fontSize: "13px" }}
                  >
                    {collaboration.profileName ?? collaboration.phoneNumber}
                  </div>
                )}
              </div>
            </div>

            <MenuAction />
          </div>

          {/* Room tabs */}
          {collaboration?.roomsModel?.length !== 0 && (
            <div className="m-0 p-0 pt-1 col-12 w-95 d-flex flex-row align-items-center justify-content-between">
              <ChatRoomTabs
                collaboration={collaboration}
                selectedRoom={selectedRoom}
                activeTab={activeTab}
                isMobile
              />
              {getHiddenButtons().length !== 0 && (
                <div className="w-[5%] align-self-center relative m-0 p-0 align-items-center justify-content-center dropdown">
                  <button
                    className="btn btn-transperent mx-1 m-0 p-0 dropdown-toggle"
                    onClick={() => setDropdownHB(!dropdownHB)}
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="bi bi-three-dots-vertical fs-4 text-black m-0 p-0"></i>
                  </button>
                  {dropdownHB && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "180px",
                        backgroundColor: "white",
                        border: "0.5px solid gray",
                        borderRadius: "8px",
                        zIndex: 200,
                        right: 0,
                      }}
                      className="dropdown-menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownHB(!dropdownHB);
                      }}
                    >
                      {getHiddenButtons().map((items) => (
                        <p
                          key={items.id}
                          className="dropdown-item text-black"
                          style={{ fontSize: "12px" }}
                        >
                          {items.buttonText}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      id="kt_chat_messenger_header"
      className="d-flex flex-column px-2 py-2 m-0 w-100"
      style={{
        height: "90px",
        flexShrink: 0, // Prevent header from shrinking
        borderStyle: "solid",
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: "0.1px",
        borderColor: "rgba(161, 165, 183, 0.5)",
      }}
    >
      <div className="col-12 p-0 m-0">
        <div className="d-flex justify-content-between p-0 m-0 w-100">
          {/* Avatar and name */}
          <div className="d-flex flex-row align-items-center h-auto p-0 m-0">
            <span
              className="symbol symbol-30px symbol-circle d-flex align-items-center py-0 my-0"
              style={{ marginRight: "6px" }}
            >
              <img
                alt={
                  collaboration?.customerModel
                    ? `${collaboration.customerModel.firstName} ${collaboration.customerModel.lastName}`
                    : defaultAvatarName
                }
                src={
                  collaboration?.profilePicture ??
                  avatarUrl(collaboration?.customerModel)
                }
                className="border border-secondary rounded-circle"
              />
            </span>
            <div className="h-auto d-flex justify-content-center flex-column">
              {contactData && contactData.id ? (
                <Link
                  to={`/contact/detail/overview/${contactData.id}`}
                  state={{
                    id: contactData.id,
                    name:
                      contactData.firstName! +
                      (contactData.lastName ? " " + contactData.lastName : ""),
                  }}
                  className="fw-semibold text-gray-900 text-hover-primary h-auto"
                  style={{ fontSize: "13px" }}
                >
                  {chatTitle}
                </Link>
              ) : (
                <div
                  className="fw-semibold text-gray-900 text-hover-primary h-auto"
                  style={{ fontSize: "13px" }}
                >
                  {collaboration.profileName ?? collaboration.phoneNumber}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="card-toolbar p-0">
            <div>
              {contactData && contactData.id ? (
                <Link
                  className="btn btn-primary btn-sm py-1 px-2"
                  to={`/contact/detail/edit/${contactData.id}`}
                  state={{
                    id: contactData.id,
                    name:
                      contactData.firstName! +
                      (contactData.lastName ? " " + contactData.lastName : ""),
                  }}
                >
                  <span style={{ fontSize: "12px" }}>
                    <i
                      className="bi bi-pencil-fill me-1"
                      style={{ fontSize: "10px" }}
                    ></i>
                    {t("HC.Button.EditContact")}
                  </span>
                </Link>
              ) : (
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm py-1 px-2 dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-display="static"
                    aria-expanded="false"
                  >
                    <span style={{ fontSize: "12px" }}>
                      <i
                        className="bi bi-pencil-fill me-1"
                        style={{ fontSize: "10px" }}
                      ></i>
                      {t("HC.Button.AddToContacts")}
                    </span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-lg-end">
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        style={{ fontSize: "12px" }}
                        onClick={onAddNewContact}
                      >
                        {t("HC.Button.AddToContacts.New")}
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        style={{ fontSize: "12px" }}
                        onClick={onAddExistingContact}
                      >
                        {t("HC.Button.AddToContacts.Existing")}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Room tabs */}
        {collaboration?.roomsModel?.length !== 0 && (
          <div className="m-0 p-0 pt-1 col-12 w-95 d-flex flex-row align-items-center justify-content-between">
            <ChatRoomTabs
              collaboration={collaboration}
              selectedRoom={selectedRoom}
              activeTab={activeTab}
            />
            {getHiddenButtons().length !== 0 && (
              <div className="w-[5%] align-self-center relative m-0 p-0 align-items-center justify-content-center dropdown">
                <button
                  className="btn btn-transperent mx-1 m-0 p-0 dropdown-toggle"
                  onClick={() => setDropdownHB(!dropdownHB)}
                  type="button"
                  id="dropdownMenuButton"
                  data-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-three-dots-vertical fs-4 text-black m-0 p-0"></i>
                </button>
                {dropdownHB && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "180px",
                      backgroundColor: "white",
                      border: "0.5px solid gray",
                      borderRadius: "8px",
                      zIndex: 200,
                      right: 0,
                    }}
                    className="dropdown-menu"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropdownHB(!dropdownHB);
                    }}
                  >
                    {getHiddenButtons().map((items) => (
                      <p
                        key={items.id}
                        className="dropdown-item text-black"
                        style={{ fontSize: "12px" }}
                      >
                        {items.buttonText}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContactHeader;
